import { type ReactNode, useMemo } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import type { EntityBadgeState } from '@/lib/crm/components/badges/types';

interface EntityElementData {
  value: string; // entity_id
  displayName: string;
  entityType: string;
}

interface EntityElementProps {
  attributes: Record<string, unknown>;
  children: ReactNode;
  element: EntityElementData;
}

const ENTITY_ARIA_LABEL_PREFIXES: Record<string, string> = {
  ahoy_user: 'View',
  ahoy_contact: 'Open contact',
  ahoy_company: 'Open company',
  ahoy_deal: 'Open deal',
};

function getAriaLabel(entityType: string, displayName: string): string {
  const prefix = ENTITY_ARIA_LABEL_PREFIXES[entityType] ?? 'View';
  return `${prefix} ${displayName}`;
}

export function EntityElement({
  attributes,
  children,
  element,
}: EntityElementProps) {
  // Memoise the badge state so identity is stable across re-renders — Plate
  // re-renders the element frequently as the editor selection changes.
  const badgeState = useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: {
        id: element.value,
        entityType: element.entityType,
        displayName: element.displayName,
      },
    }),
    [element.value, element.entityType, element.displayName]
  );
  return (
    <span
      {...attributes}
      contentEditable={false}
      role='button'
      aria-label={getAriaLabel(element.entityType, element.displayName)}
    >
      <EntityBadge variant='inline' interactive={false} state={badgeState} />
      {children}
    </span>
  );
}
